import {
  Button,
  Center,
  HStack,
  IconButton,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spacer,
  Spinner,
  Stack,
  Text,
} from '@chakra-ui/react';

import {
  CloseCircleOutline as CloseCircle,
  DeleteOutline as Delete,
  CheckOutline,
  CustomAlert,
  useToast,
} from '@aslanpay/portal-common-lib';

import useEndUnpaidLeave from 'app/hooks/useEndUnpaidLeave';
import useFetchIndividual from 'app/hooks/useFetchIndividual';
import useStartUnpaidLeave from 'app/hooks/useStartUnpaidLeave';

import { ManageUnpaidLeaveModalProps } from './types';

function ManageUnpaidLeaveModal({
  id,
  isOpen,
  onClose,
  onUnpaidLeave,
}: ManageUnpaidLeaveModalProps) {
  const toast = useToast();

  const { data: employee, isFetching: isLoadingEmployee } = useFetchIndividual(id, isOpen);

  const { mutate: endUnpaidLeave, isLoading: isEndingUnpaidLeave } = useEndUnpaidLeave({
    onSuccess: () => {
      toast({
        position: 'top-right',
        status: 'success',
        title: 'Accrual pause removed',
        description: `The employee ${employee?.firstName} ${employee?.lastName} is no longer on accrual pause.`,
      });

      onUnpaidLeave();
      onClose();
    },
    onError: () => {
      toast({
        position: 'top-right',
        status: 'error',
        title: 'Something went wrong',
        description: 'Sorry, the employee is still on accrual pause. Please try again.',
      });
    },
  });

  const { mutate: startUnpaidLeave, isLoading: isStartingUnpaidLeave } = useStartUnpaidLeave({
    onSuccess: () => {
      toast({
        position: 'top-right',
        status: 'success',
        title: 'Accrual pause set',
        description: 'The employee will be marked as on accrual pause from today.',
      });

      onUnpaidLeave();
      onClose();
    },
    onError: () => {
      toast({
        position: 'top-right',
        status: 'error',
        title: 'Something went wrong',
        description:
          'Sorry, the employee account was not marked as on accrual pause. Please try again.',
      });
    },
  });

  const title = employee?.onUnpaidLeave ? 'Remove accrual pause' : 'Set accrual pause';

  return (
    <Modal
      size="lg"
      isCentered
      isOpen={isOpen}
      onClose={onClose}
      closeOnEsc={false}
      closeOnOverlayClick={false}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <HStack>
            <Text as="h6">{title}</Text>
            <Spacer />
            <IconButton
              variant="link"
              icon={<CloseCircle boxSize="30px" />}
              aria-label="Manage employee accrual pause"
              onClick={onClose}
            />
          </HStack>
        </ModalHeader>
        {isLoadingEmployee ? (
          <Center width="full" height="full" flexGrow={1}>
            <Spinner color="brand.purple" size="xl" mb="50px" />
          </Center>
        ) : (
          <>
            <ModalBody mt="20px">
              <Stack spacing="20px">
                <CustomAlert
                  variant="simple"
                  status="info"
                  title={
                    <>
                      <Text textStyle="extraSmallMedium">What is the accrual pause?</Text>
                      <Text textStyle="extraSmallRegular">
                        This is to mark that the employee is starting an accrual pause and will no
                        longer accrue a salary. The employee can still access all Aslan services and
                        use their card. You can remove the accrual pause at any time.
                      </Text>
                    </>
                  }
                />
                {employee?.onUnpaidLeave ? (
                  <Text color="brand.mutedPurple" textStyle="body">
                    You are about to remove accrual pause for{' '}
                    <Text as="span" textStyle="h6SemiBold">
                      {employee?.firstName
                        ? `${employee?.firstName} ${employee?.lastName}`
                        : employee?.email}
                    </Text>
                    . We will consider this employee as returned to work from today.
                  </Text>
                ) : (
                  <Text color="brand.mutedPurple" textStyle="body">
                    You are about to set accrual pause for{' '}
                    <Text as="span" textStyle="h6SemiBold">
                      {employee?.firstName
                        ? `${employee?.firstName} ${employee?.lastName}`
                        : employee?.email}
                    </Text>
                    . Starting today, we will consider this employee not working.
                  </Text>
                )}
              </Stack>
            </ModalBody>
            <ModalFooter>
              {employee?.onUnpaidLeave ? (
                <>
                  <Button mr="20px" variant="link" size="sm" onClick={onClose}>
                    Cancel
                  </Button>
                  <Button
                    className="danger"
                    variant="outline"
                    size="sm"
                    leftIcon={<Delete boxSize="20px" />}
                    isLoading={isEndingUnpaidLeave}
                    onClick={() => endUnpaidLeave({ id })}
                  >
                    Remove
                  </Button>
                </>
              ) : (
                <>
                  <Button variant="outline" size="sm" onClick={onClose}>
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    rightIcon={<CheckOutline boxSize="20px" />}
                    isLoading={isStartingUnpaidLeave}
                    onClick={() => startUnpaidLeave({ id })}
                  >
                    Set
                  </Button>
                </>
              )}
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
}

export default ManageUnpaidLeaveModal;
